import React, { Component } from 'react';

import { Eye, EyeClosed } from '../common/icons/icons';

import { Card, CardTitle, Wrapper, ErrorMessage, Input, Row, SubmitButton, Text, Hr, PassEye } from './styled/LoginStyled';

import useLocalization from '../../localization/useLocalization';
import { loadLoginInfo } from '../../utils/login';
import { colors } from '../../utils/utils';

const preSavedLoginInfo = loadLoginInfo();

interface LoginProps {
    doLogin: (locationName: string, username: string, password: string) => void;
    pending: boolean;
    error: string;
    localization: { getText: (key: string) => string };
}

interface LoginState {
    locationName: string;
    username: string;
    password: string;
    showPassword: boolean;
}

class LoginView extends Component<LoginProps, LoginState> {
    state = {
        locationName: (preSavedLoginInfo && preSavedLoginInfo.locationName) || '',
        username: (preSavedLoginInfo && preSavedLoginInfo.username) || '',
        password: (preSavedLoginInfo && preSavedLoginInfo.password) || '',
        showPassword: false
    };

    isValid() {
        const { locationName, password, username } = this.state;
        return locationName !== '' && password !== '' && username !== '';
    }

    componentDidMount() {
        if (this.isValid()) {
            const { locationName, password, username } = this.state;
            this.props.doLogin(locationName, username, password);
        }
    }

    render() {
        const { pending, doLogin, localization, error } = this.props;
        const { locationName, password, username, showPassword } = this.state;

        const isValid = this.isValid();

        const login = () => {
            if (isValid) {
                doLogin(locationName, username, password);
            }
        };

        const onChange = (values: Partial<LoginState>) => {
            this.setState(values as Pick<LoginState, keyof LoginState>);
        };

        return (
            <Wrapper>
                <Card $enabled={!pending}>
                    <CardTitle>{localization.getText('login.title')}</CardTitle>
                    <div>
                        {!!error && (
                            <div style={{ marginTop: 12 }}>
                                <ErrorMessage>{localization.getText(error)}</ErrorMessage>
                            </div>
                        )}
                        <Row>
                            <Input
                                autoFocus={!this.state.locationName}
                                type="text"
                                value={this.state.locationName}
                                disabled={pending}
                                placeholder={localization.getText('login.locationName')}
                                onChange={({ target }) => onChange({ locationName: target.value })}
                                onKeyDown={e => {
                                    if (e && e.keyCode === 13) {
                                        login();
                                    }
                                }}
                            />
                        </Row>
                        <Row>
                            <Input
                                autoFocus={!!this.state.locationName}
                                type="text"
                                value={this.state.username}
                                disabled={pending}
                                placeholder={localization.getText('login.username')}
                                onChange={({ target }) => onChange({ username: target.value })}
                                onKeyDown={e => {
                                    if (e && e.keyCode === 13) {
                                        login();
                                    }
                                }}
                            />
                        </Row>
                        <Row>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={this.state.password}
                                disabled={pending}
                                placeholder={localization.getText('login.password')}
                                onChange={({ target }) => onChange({ password: target.value })}
                                onKeyDown={e => {
                                    if (e && e.keyCode === 13) {
                                        login();
                                    }
                                }}
                            />
                            <PassEye
                                onClick={() =>
                                    this.setState(prevState => ({
                                        showPassword: !prevState.showPassword
                                    }))
                                }>
                                {showPassword ? (
                                    <Eye size={30} color={colors.gray.silver_sand} />
                                ) : (
                                    <EyeClosed size={30} color={colors.gray.silver_sand} />
                                )}
                            </PassEye>
                        </Row>
                        <SubmitButton
                            onClick={() => {
                                login();
                            }}
                            disabled={!isValid}>
                            {localization.getText('login.doLogin')}
                        </SubmitButton>
                        <Hr />
                        <Text>
                            {localization.getText('login.troubleSigningIn')}
                            <br />
                            {localization.getText('login.contactManager')}
                        </Text>
                    </div>
                </Card>
            </Wrapper>
        );
    }
}

function Login(props: Omit<LoginProps, 'localization'>) {
    const localization = useLocalization();
    const { doLogin, pending, error } = props;

    return <LoginView doLogin={doLogin} pending={pending} error={error} localization={localization} />;
}

export default Login;
